import { useEffect, useRef, useState } from 'react';

import { Button, Input, Alert, AlertDescription } from '@klaro7/ui';

import { useRequestPhoneCode, useConfirmPhoneCode } from '../hooks/usePhoneVerification';
import { PhonePrefixInput, type PhoneFieldTone } from './PhonePrefixInput';

interface ApiErrorBody {
  code?: string;
  message?: string;
  meta?: Record<string, unknown>;
}

function extractApiError(err: unknown): ApiErrorBody {
  const error = (err as { response?: { data?: { error?: ApiErrorBody } } })?.response?.data?.error;
  return error ?? {};
}

function extractError(err: unknown): string {
  const apiMsg = extractApiError(err).message;
  if (apiMsg) return apiMsg;
  return err instanceof Error ? err.message : 'Errore';
}

function metaNumber(meta: Record<string, unknown> | undefined, key: string): number | undefined {
  const v = meta?.[key];
  return typeof v === 'number' ? v : undefined;
}

const E164 = /^\+[1-9]\d{6,14}$/;

export function PhoneVerification({
  initialPhone = '',
  onVerified,
  autoStart = false,
  tone = 'light',
}: {
  initialPhone?: string;
  onVerified?: () => void;
  /** When true and initialPhone is a valid E.164 number, requests the OTP on
   * mount and jumps straight to the code-entry stage. */
  autoStart?: boolean;
  /** `dark` renders the form directly on the navy auth background (no white card). */
  tone?: PhoneFieldTone;
}) {
  const [phone, setPhone] = useState(initialPhone);
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'phone' | 'code' | 'done'>('phone');
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const autoStarted = useRef(false);

  const requestMut = useRequestPhoneCode();
  const confirmMut = useConfirmPhoneCode();

  // Auto-send the OTP once when opened with a valid number already provided.
  useEffect(() => {
    if (autoStart && !autoStarted.current && stage === 'phone' && E164.test(initialPhone.trim())) {
      autoStarted.current = true;
      void handleRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  async function handleRequest() {
    setError(null);
    try {
      const res = await requestMut.mutateAsync(phone.trim());
      setResendIn(res.resendInSeconds);
      setStage('code');
    } catch (err) {
      const api = extractApiError(err);
      if (api.code === 'OTP_COOLDOWN') {
        // Server enforces the resend cooldown; reflect its countdown.
        // The user stays on whatever stage they were on (a code was already sent).
        const secondsLeft = metaNumber(api.meta, 'secondsLeft');
        if (secondsLeft !== undefined) setResendIn(secondsLeft);
      }
      setError(extractError(err));
    }
  }

  async function handleConfirm() {
    setError(null);
    try {
      await confirmMut.mutateAsync(code.trim());
      setStage('done');
      onVerified?.();
    } catch (err) {
      const api = extractApiError(err);
      if (api.code === 'OTP_INVALID') {
        const attemptsLeft = metaNumber(api.meta, 'attemptsLeft');
        if (attemptsLeft !== undefined) {
          setError(`${extractError(err)} Tentativi rimasti: ${attemptsLeft}`);
          return;
        }
      }
      setError(extractError(err));
    }
  }

  const isDark = tone === 'dark';

  if (stage === 'done') {
    if (isDark) {
      return (
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3.5 py-2.5 text-sm text-emerald-200">
          Telefono verificato ✓
        </div>
      );
    }
    return (
      <Alert variant="success">
        <AlertDescription>Telefono verificato ✓</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {error &&
        (isDark ? (
          <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-200">
            {error}
          </div>
        ) : (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {stage === 'phone' ? (
        <div className="space-y-3">
          <PhonePrefixInput
            label="Numero di telefono"
            value={phone}
            onChange={setPhone}
            tone={tone}
          />
          {isDark ? (
            <DarkPrimaryButton
              onClick={handleRequest}
              loading={requestMut.isPending}
              disabled={!E164.test(phone.trim())}
            >
              Invia codice
            </DarkPrimaryButton>
          ) : (
            <Button
              onClick={handleRequest}
              loading={requestMut.isPending}
              disabled={!E164.test(phone.trim())}
              className="w-full"
            >
              Invia codice
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {isDark ? (
            <div className="space-y-1.5">
              <label htmlFor="otp-code" className="block text-sm font-medium text-white/70">
                Codice (4 cifre)
              </label>
              <input
                id="otp-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="0000"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 text-center text-lg tracking-[0.5em] text-white placeholder:text-white/25 transition-colors focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
              />
            </div>
          ) : (
            <Input
              label="Codice (4 cifre)"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              inputMode="numeric"
            />
          )}

          {isDark ? (
            <div className="space-y-2">
              <DarkPrimaryButton
                onClick={handleConfirm}
                loading={confirmMut.isPending}
                disabled={code.length !== 4}
              >
                Verifica
              </DarkPrimaryButton>
              <button
                type="button"
                onClick={handleRequest}
                disabled={resendIn > 0 || requestMut.isPending}
                className="h-10 w-full rounded-lg border border-white/15 text-sm text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white disabled:pointer-events-none disabled:opacity-50"
              >
                {resendIn > 0 ? `Reinvia tra ${resendIn}s` : 'Reinvia codice'}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                onClick={handleConfirm}
                loading={confirmMut.isPending}
                disabled={code.length !== 4}
              >
                Verifica
              </Button>
              <Button
                variant="outline"
                onClick={handleRequest}
                disabled={resendIn > 0 || requestMut.isPending}
              >
                {resendIn > 0 ? `Reinvia tra ${resendIn}s` : 'Reinvia codice'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Primary CTA on the navy auth background — same teal gradient as the login page. */
function DarkPrimaryButton({
  onClick,
  loading,
  disabled,
  children,
}: {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className="flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-brand-teal/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 focus-visible:ring-offset-[#231F55] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
      style={{ background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)' }}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
